import { useState } from "react";
import type { FormEvent } from "react";
import { formPurposeNote } from "@/mocks/homeContent";

const FORM_SUBMIT = "https://readdy.ai/api/form/dagldo3ep053ijub2cl0";

type Status = "idle" | "submitting" | "success" | "error";

export default function Contact() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = (fd.get("company_alt") as string) || "";
    if (honeypot.trim().length > 0) {
      setStatus("success");
      form.reset();
      return;
    }

    const type = (fd.get("entry_type") as string) || "公开投稿";
    const category = (fd.get("category") as string) || "";
    const title = (fd.get("title") as string) || "";
    const desc = (fd.get("detail") as string) || "";
    const contact = (fd.get("contact") as string) || "";

    const payload: Record<string, string> = {};
    if (type) payload.entry_type = type;
    if (category) payload.category = category;
    if (title) payload.title = title;
    if (desc) payload.detail = desc;
    if (contact) payload.contact = contact;

    setStatus("submitting");
    setFormError("");

    try {
      const res = await fetch(FORM_SUBMIT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(payload).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta ?? {}) as Record<string, unknown>;
      const code = parsed?.code as string | undefined;
      const message =
        typeof meta.message === "string"
          ? meta.message
          : typeof parsed?.message === "string"
            ? parsed.message
            : "";
      const isSpam =
        message.toLowerCase().includes("spam") ||
        responseText.toLowerCase().includes("spam") ||
        String(meta.detail ?? "").toLowerCase().includes("spam");

      if (res.ok && code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(message || "提交未能成功，请稍后再试，或更换信息完整度后再提交。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，暂时无法提交，请稍后重试。");
    }
  }

  return (
    <section id="contact" className="w-full bg-background-100 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
          <div className="flex flex-col justify-center">
            <span className="inline-flex items-center gap-2 text-sm font-medium text-primary-700">
              <span className="h-px w-6 bg-primary-600"></span> 提交线索 / 联系我们
            </span>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-foreground-950 md:text-4xl">
              准备好要提交的内容了吗？
            </h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-600">
              按下述表单填写即可进入受理流程。若材料较大或内容敏感，可先提交概要，编辑将主动与你就材料补充方式取得联系。
            </p>

            <div className="mt-8 space-y-4">
              {[
                { icon: "ri-shield-keyhole-fill", t: "请勿提交他人隐私或敏感个人信息" },
                { icon: "ri-file-shield-2-fill", t: "尽量提供时间、地点、人物与一手出处" },
                { icon: "ri-time-line", t: "常规线索将在工作日 24 小时内回执" },
              ].map((n) => (
                <div key={n.t} className="flex items-start gap-3 rounded-lg bg-background-50 p-4">
                  <i className={`${n.icon} mt-0.5 text-xl text-primary-600`}></i>
                  <p className="text-sm leading-relaxed text-foreground-700">{n.t}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            {status === "success" ? (
              <div className="flex h-full flex-col items-center justify-center py-14 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                  <i className="ri-check-line text-3xl"></i>
                </span>
                <h3 className="mt-5 font-display text-xl font-bold text-foreground-950">
                  已收到你的提交
                </h3>
                <p className="mt-2 max-w-sm text-sm text-foreground-600">
                  编辑部会依据你留下的联系方式与你对接。感谢你把线索交给我们。
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-6 inline-flex items-center gap-2 rounded-md border border-background-200 px-4 py-2 text-sm text-foreground-700 whitespace-nowrap hover:bg-background-100"
                >
                  再次提交
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form className="space-y-5">
                <div>
                  <label htmlFor="entry_type" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    选择入口 *
                  </label>
                  <select
                    id="entry_type"
                    name="entry_type"
                    required
                    defaultValue="公开投稿"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                  >
                    <option value="爆料入口">爆料入口</option>
                    <option value="公开投稿">公开投稿</option>
                    <option value="线索征集">线索征集</option>
                    <option value="知情者通道">知情者通道</option>
                    <option value="合作洽谈">合作洽谈</option>
                    <option value="其他咨询">其他咨询</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="category" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    内容分类
                  </label>
                  <select
                    id="category"
                    name="category"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                  >
                    <option value="">请选择（可选）</option>
                    <option value="公共事件">公共事件</option>
                    <option value="行业观察">行业观察</option>
                    <option value="民生议题">民生议题</option>
                    <option value="图文投稿">图文投稿</option>
                    <option value="音视频素材">音视频素材</option>
                    <option value="合作与转载">合作与转载</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="title" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    主题概述 *
                  </label>
                  <input
                    id="title"
                    name="title"
                    type="text"
                    required
                    maxLength={80}
                    placeholder="用一句话说明你希望提交的核心内容"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition placeholder:text-foreground-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                  />
                </div>

                <div>
                  <label htmlFor="detail" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    内容说明 *
                  </label>
                  <textarea
                    id="detail"
                    name="detail"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="补充时间、地点、涉及主体与可提供的材料类型，越具体越便于核验"
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition placeholder:text-foreground-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                  ></textarea>
                  <p className="mt-1 text-right text-xs text-foreground-400">
                    限 500 字以内
                  </p>
                </div>

                <div>
                  <label htmlFor="contact" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    联系方式 *
                  </label>
                  <input
                    id="contact"
                    name="contact"
                    type="text"
                    required
                    maxLength={60}
                    placeholder="用于编辑部与你对接的邮箱或账号（可在下文选择匿名）"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition placeholder:text-foreground-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                  />
                </div>

                {/* 反垃圾蜜罐字段 */}
                <input
                  name="company_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <p className="rounded-md bg-secondary-100 px-3.5 py-2.5 text-xs leading-relaxed text-secondary-900">
                  {formPurposeNote}
                </p>

                {status === "error" && formError && (
                  <p className="rounded-md bg-primary-100 px-3.5 py-2.5 text-sm text-primary-900">
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-600 px-5 py-3 text-sm font-medium text-background-50 whitespace-nowrap transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      正在提交…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill"></i>
                      提交线索
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}